import { useMutation } from "@apollo/client/react";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MARK_NOTIFICATION_READ } from "@graphql/mutations/MarkNotificationRead";
import { useVisibleNotifications } from "@hooks";
import { animated, config, useSpring } from "@react-spring/web";
import { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router";

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const MAX_PREVIEW = 8;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  const { visible: visibleNotifications, markBroadcastRead } =
    useVisibleNotifications({
      domain: "scribe",
      unreadOnly: true,
    });

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(forceUpdate, 60_000);
    return () => clearInterval(id);
  }, []);

  const [markRead] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries: ["userNotifications"],
  });

  const unread = visibleNotifications
    .filter((n) => !n.readAt)
    .slice()
    .sort(
      (a, b) =>
        new Date(b.activeAt ?? b.createdAt).getTime() -
        new Date(a.activeAt ?? a.createdAt).getTime(),
    );

  const preview = unread.slice(0, MAX_PREVIEW);
  const count = unread.length;

  const hasAny = visibleNotifications.length > 0;

  useEffect(() => {
    const handler = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const menuAnim = useSpring({
    config: config.stiff,
    opacity: open ? 1 : 0,
    transform: open ? "scale(1)" : "scale(0.95)",
  });

  if (!hasAny) return null;

  const onItemClick = (n) => {
    if (n.isBroadcast) {
      markBroadcastRead(n.uuid);
    } else {
      markRead({ variables: { notificationUuid: n.uuid } }).catch(
        () => {},
      );
    }
    if (n.actionUrl) {
      setOpen(false);
      if (/^https?:\/\//i.test(n.actionUrl)) {
        window.open(n.actionUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(n.actionUrl);
      }
      return;
    }
    if (n.noteUuid) {
      setOpen(false);
      navigate(`/edit_note/${n.noteUuid}`);
    }
  };

  return (
    <div ref={wrapperRef} className="relative inline-block text-left">
      <button
        type="button"
        aria-label="Notifications"
        data-testid="notification-bell-button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center h-10 w-10 rounded-full hover:bg-secondary transition-colors focus:outline-hidden"
      >
        <FontAwesomeIcon icon={faBell} className="text-primary" />
        {count > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      <animated.div
        style={menuAnim}
        className={
          open
            ? "origin-top-right absolute right-0 mt-2 w-80 rounded-standard shadow-lg bg-secondary divide-y divide-secondary z-50"
            : "hidden"
        }
        role="menu"
      >
        <div className="px-4 py-3 flex items-center justify-between">
          <h4 className="text-primary font-semibold text-sm">
            Notifications
          </h4>
          <span className="text-xs text-secondary">
            {count} unread
          </span>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {preview.length === 0 ? (
            <div className="px-4 py-6 text-sm text-secondary text-center">
              No new notifications
            </div>
          ) : (
            preview.map((n) => (
              <div
                key={n.uuid}
                role="menuitem"
                onClick={() => onItemClick(n)}
                className="px-4 py-3 cursor-pointer hover:bg-tertiary"
                data-testid="notification-bell-item"
              >
                {n.title && (
                  <div className="text-sm font-medium text-primary truncate">
                    {n.title}
                  </div>
                )}
                {n.message && (
                  <div className="text-xs text-secondary line-clamp-2 mt-0.5">
                    {n.message}
                  </div>
                )}
                <div className="text-xs text-secondary mt-1">
                  {timeAgo(n.activeAt ?? n.createdAt)}
                </div>
              </div>
            ))
          )}
        </div>

        <div
          role="menuitem"
          onClick={() => {
            setOpen(false);
            navigate("/notifications");
          }}
          className="px-4 py-2 text-sm text-brand text-center cursor-pointer hover:bg-tertiary"
          data-testid="notification-bell-see-all"
        >
          See all
        </div>
      </animated.div>
    </div>
  );
};

export default NotificationBell;
